/**
 * Screen Interface System - Type Definitions
 *
 * Standardized interfaces for screen state management.
 * Enforces good development practices with loading, success, error, and empty states.
 */

import { ResponseCode } from '@/business/types/response-codes.types';

/**
 * Screen state enum representing the lifecycle of a screen
 */
export enum ScreenState {
  /** Initial state when screen is mounting */
  INITIALIZING = 'INITIALIZING',
  /** Loading initial data or refreshing */
  LOADING = 'LOADING',
  /** Action in progress (for button clicks, form submissions, etc.) */
  IN_PROGRESS = 'IN_PROGRESS',
  /** Successfully loaded with data */
  SUCCESS = ResponseCode.SUCCESS,
  /** Error occurred */
  ERROR = 'ERROR',
  /** No data to display */
  EMPTY = 'EMPTY',
  /** Ready for new actions (after error or success) */
  IDLE = 'IDLE',
}

/**
 * Screen types for identification and routing
 */
export enum ScreenType {
  LOGIN = 'LOGIN',
  DASHBOARD = 'DASHBOARD',
  COMING_SOON = 'COMING_SOON',
  BOARD = 'BOARD',
  PROFILE = 'PROFILE',
  SETTINGS = 'SETTINGS',
  MANAGE_PEOPLE = 'MANAGE_PEOPLE',
  ORGS_MANAGEMENT = 'ORGS_MANAGEMENT',
  CONFIG_MANAGEMENT = 'CONFIG_MANAGEMENT',
  BOARD_TEMPLATES_MANAGEMENT = 'BOARD_TEMPLATES_MANAGEMENT',
  SCHEDULED_JOBS = 'SCHEDULED_JOBS',
  JOB_RUN_DETAIL = 'JOB_RUN_DETAIL',
  SYSTEM_HEALTH = 'SYSTEM_HEALTH',
  GROUPS = 'GROUPS',
}

/**
 * Error information for screen error states
 */
export interface ScreenError {
  /** Response code from the action */
  code: ResponseCode;
  /** User-friendly error message */
  message: string;
  /** Additional error details (optional) */
  details?: string;
  /** Whether this error can be retried */
  canRetry: boolean;
  /** Optional function to call for retry */
  retryAction?: () => void;
}

/**
 * Screen metadata for configuration
 */
export interface ScreenMetadata {
  /** Type of screen */
  screenType: ScreenType;
  /** Display title (optional - if not provided, tab name is shown) */
  title?: string;
  /** Whether authentication is required */
  requiresAuth: boolean;
  /** Whether to show header */
  showHeader?: boolean;
  /** Whether to show sidebar */
  showSidebar?: boolean;
}

/**
 * Data loading strategy for screens
 */
export enum LoadStrategy {
  /** Load from cache first, fallback to API if cache miss */
  CACHE_FIRST = 'CACHE_FIRST',
  /** Load from API first, update cache */
  API_FIRST = 'API_FIRST',
  /** Load from cache only, no API call */
  CACHE_ONLY = 'CACHE_ONLY',
  /** Load from API only, no cache */
  API_ONLY = 'API_ONLY',
}

/**
 * Screen lifecycle callbacks (optional event handlers)
 */
export interface ScreenLifecycle {
  /** Called when screen mounts (after load completes) */
  onMount?: () => void | Promise<void>;
  /** Called when screen unmounts */
  onUnmount?: () => void;
  /** Called when an error occurs */
  onError?: (error: ScreenError) => void;
  /** Called after successful load */
  onLoadSuccess?: () => void;
  /** Called after load fails */
  onLoadError?: (error: ScreenError) => void;
}

/**
 * Required screen implementation interface.
 * All screens MUST implement the load() method.
 */
export interface IScreenImplementation {
  /**
   * Load screen data.
   * This method MUST be implemented by every screen.
   *
   * @param strategy Loading strategy (cache-first or API-first)
   * @returns Promise that resolves when loading completes
   */
  load(strategy?: LoadStrategy): Promise<void>;
}

/**
 * Base interface that defines screen contract
 */
export interface IScreen extends IScreenImplementation {
  /** Screen metadata */
  metadata: ScreenMetadata;
  /** Screen lifecycle callbacks (optional) */
  lifecycle?: ScreenLifecycle;
}

/**
 * Configuration for loading skeleton
 */
export interface SkeletonConfig {
  /** Type of skeleton to display */
  type: 'card' | 'list' | 'table' | 'form' | 'dashboard' | 'kanban' | 'custom';
  /** Number of skeleton items to show */
  count?: number;
  /** Custom skeleton component */
  CustomSkeleton?: React.ComponentType;
}

/**
 * Configuration for empty state
 */
export interface EmptyStateConfig {
  /** Title for empty state */
  title: string;
  /** Description text */
  description: string;
  /** Icon component or SVG */
  icon?: React.ReactNode;
  /** Action button text */
  actionText?: string;
  /** Action button handler */
  onAction?: () => void;
}

/**
 * Configuration for error state
 */
export interface ErrorStateConfig {
  /** Title for error state */
  title?: string;
  /** Description text (defaults to error message) */
  description?: string;
  /** Whether to show retry button */
  showRetry?: boolean;
  /** Custom retry text */
  retryText?: string;
  /** Whether to show contact support link */
  showSupport?: boolean;
}

/**
 * Return type for useScreenState hook
 */
export interface ScreenStateHook {
  /** Current screen state */
  state: ScreenState;
  /** Error object (if in ERROR state) */
  error: ScreenError | null;
  /** Progress value (0-100) */
  progress: number;

  // State transitions
  /** Set state to LOADING */
  setLoading: () => void;
  /** Set state to SUCCESS */
  setSuccess: () => void;
  /** Set state to ERROR with error details */
  setError: (error: ScreenError) => void;
  /** Set state to EMPTY */
  setEmpty: () => void;
  /** Set state to IDLE */
  setIdle: () => void;
  /** Set state to IN_PROGRESS with optional progress */
  setInProgress: (progress?: number) => void;

  // Derived states
  /** Whether screen is loading */
  isLoading: boolean;
  /** Whether screen has error */
  isError: boolean;
  /** Whether screen is successful */
  isSuccess: boolean;
  /** Whether screen is empty */
  isEmpty: boolean;
  /** Whether action is in progress */
  isInProgress: boolean;

  // Required screen methods
  /**
   * Load screen data with specified strategy.
   * This calls the screen's load implementation.
   */
  load: (strategy?: LoadStrategy) => Promise<void>;

  /**
   * Reload screen data (alias for load with same strategy as last call).
   */
  reload: () => Promise<void>;

  // Actions
  /** Reset state to INITIALIZING */
  reset: () => void;
  /** Retry the last failed action (calls load again) */
  retry: () => void;
}

/**
 * Options for useScreenWithOrchestrator hook
 */
export interface ScreenWithOrchestratorOptions {
  /** Screen metadata */
  metadata: ScreenMetadata;
  /** Screen lifecycle callbacks */
  lifecycle?: ScreenLifecycle;
  /** Whether to automatically execute on mount */
  autoExecuteOnMount?: boolean;
}
